'use client'

import { AlertCircle, CloudUpload } from 'lucide-react'
import type React from 'react'
import { useCallback, useRef } from 'react'

import { useToast } from '@/components/ui'
import { cn } from '@/lib/cn'
import type { FileType } from '@/types/fileType'
import { mimeTypeToExtensions } from '@/utils/mimeTypeToExtension'

export type DropzoneProps = {
  files: FileType[]
  onChange: (files: FileType[]) => void
  className?: string
  mimeType?: string[]
  error?: string
  description?: string
  maxFile?: number
  maxSizeInMB?: number
}

const Dropzone = ({
  files,
  onChange,
  className,
  mimeType,
  error,
  description = 'Arraste uma imagem aqui ou clique para buscar',
  maxFile = 3,
  maxSizeInMB = 3,
}: DropzoneProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const { toast } = useToast()

  const handleFiles = useCallback(
    (fileList: FileList) => {
      const validFiles: FileType[] = []
      const invalidFiles: FileType[] = []
      const maxSize = maxSizeInMB * 1024 * 1024 // Converter MB para bytes

      const currentFileCount = files.length
      const filesRemaining = maxFile - currentFileCount

      if (filesRemaining <= 0) {
        toast({
          title: 'Limite de arquivos atingido',
          description: 'Você atingiu o limite de arquivos permitidos',
          variant: 'destructive',
          icon: AlertCircle,
        })
        return
      }

      const filesToProcess = Array.from(fileList)

      for (let i = 0; i < filesToProcess.length; i++) {
        if (validFiles.length >= filesRemaining) {
          break
        }

        const uploadedFile = filesToProcess[i]
        let isValid = true

        if (uploadedFile.size > maxSize) {
          invalidFiles.push({ file: uploadedFile })
          isValid = false
          continue
        }

        if (mimeType) {
          const uploadedMimeType = uploadedFile.type
          const isValidMimeType = mimeType.some((type) => {
            if (type.endsWith('/*')) {
              const [baseType] = type.split('/')
              return uploadedMimeType.startsWith(baseType)
            }

            return uploadedMimeType === type
          })

          if (!isValidMimeType) {
            invalidFiles.push({ file: uploadedFile })
            isValid = false
            continue
          }
        }

        if (isValid && validFiles.length < filesRemaining) {
          validFiles.push({ file: uploadedFile })
        }
      }

      onChange([...files, ...validFiles])

      if (invalidFiles.length) {
        const errorMessage = `Arquivos inválidos\n${invalidFiles
          .map(({ file }) => file?.name)
          .join('\n')}`
        toast({
          title: 'Arquivos inválidos',
          description: errorMessage,
          variant: 'destructive',
          icon: AlertCircle,
        })
      }
    },
    [maxSizeInMB, files, maxFile, onChange, toast, mimeType],
  )

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
    },
    [],
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const { files } = event.dataTransfer
      handleFiles(files)
    },
    [handleFiles],
  )

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target

      if (files) {
        handleFiles(files)
      }
    },
    [handleFiles],
  )

  const handleButtonClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (fileInputRef.current) {
        fileInputRef.current.click()
      }
    },
    [],
  )

  return (
    <div className="flex w-full flex-col gap-2" data-testid="dropzone">
      <div
        className={cn(
          'border-2 border-dashed hover:cursor-pointer hover:border-muted-foreground/50 rounded-md',
          error && 'border-destructive/50 hover:border-destructive',
          className,
        )}
        onClick={handleButtonClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center justify-center gap-4 space-y-2 px-2 py-12">
          <div className="flex flex-col items-center gap-2 text-center">
            <CloudUpload size={36} className="text-system-primary-icon" />
            <span className="font-normal text-system-text-primary">
              {description}
            </span>
            <span className="font-inter text-sm font-normal text-system-text-secondary">
              É permitido no max. {maxFile} arquivos e no max. {maxSizeInMB}
              MB
            </span>
            {mimeType && (
              <span className="text-xs">
                Extensões permitidas {mimeTypeToExtensions(mimeType).join(', ')}
              </span>
            )}
          </div>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={mimeType?.join(', ')}
        onChange={handleFileInputChange}
        className="hidden"
        multiple
      />

      {error && (
        <span className="text-sm font-medium text-destructive">{error}</span>
      )}
    </div>
  )
}
Dropzone.displayName = 'Dropzone'

export { Dropzone }
